/**
 * Decléor — comparison table: the card you are reading comes forward (phones only).
 *
 * Under 900px the comparison table stops being a grid and becomes a stack of cards — one per product,
 * each cell printing its own column header. A stack gives the eye nothing to hold on to, so this
 * writes `--dc-pt-focus` on every row: 1 when the card is centred in the viewport, falling to 0 as it
 * leaves. snippets/richtext-blocks-style.liquid turns that into a small lift and a dim.
 *
 * WHAT IT DELIBERATELY IS NOT: a scroll listener. An IntersectionObserver with a ladder of thresholds
 * reports only when a card CROSSES one, so the work happens on the frames where something actually
 * changed and nothing at all while the page is still — where a scroll handler would run on every
 * frame of every scroll for every row. The cost is a coarser value; 21 steps is finer than the 0.015
 * of scale and the 0.55 of opacity it drives can express.
 *
 * The value is distance from the viewport's middle, not intersection ratio: a tall card that fills the
 * screen is 100% intersecting at every position, and ratio alone would light two neighbours equally.
 * The rect the observer hands back is free — no getBoundingClientRect of our own, no forced layout.
 *
 * Runs only where the stack exists (a media query the script asks the DOM about, so the breakpoint
 * lives in the stylesheet alone), only on tables that asked for it, and never under reduced motion.
 */
(function () {
  'use strict';

  if (typeof IntersectionObserver !== 'function') {
    return;
  }
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  var tables = document.querySelectorAll('.dc-ptable[data-pt-focus]');
  if (!tables.length) {
    return;
  }

  var thresholds = [];
  var t;
  for (t = 0; t <= 20; t += 1) {
    thresholds.push(t / 20);
  }

  /* The stylesheet owns the breakpoint. Asking whether the first cell is laid out as a block — which
     is what the stacked-card rules do and what the table layout never does — means this and the CSS
     cannot disagree about where "phone" starts. */
  function stacked(table) {
    var cell = table.querySelector('tbody th');
    return !!cell && window.getComputedStyle(cell).display === 'block';
  }

  function apply(entry) {
    var row = entry.target;
    var rect = entry.boundingClientRect;
    var middle = window.innerHeight / 2;
    var centre = rect.top + rect.height / 2;
    /* Full weight within a quarter-screen of the middle, then falling away to nothing by the edge.
       The half-screen span is what stops two adjacent cards being lit at once on a tall phone. */
    var distance = Math.abs(centre - middle) / (window.innerHeight * 0.5);
    var focus = 1 - Math.max(0, Math.min(1, distance));
    var next = String(Math.round(focus * 100) / 100);
    if (row.__ptFocus !== next) {
      row.__ptFocus = next;
      row.style.setProperty('--dc-pt-focus', next);
    }
  }

  function watch(table) {
    var rows = table.querySelectorAll('tbody tr');
    var observer = new IntersectionObserver(
      function (entries) {
        var i;
        for (i = 0; i < entries.length; i += 1) {
          apply(entries[i]);
        }
      },
      { threshold: thresholds }
    );
    var i;
    for (i = 0; i < rows.length; i += 1) {
      observer.observe(rows[i]);
    }
    return observer;
  }

  var watching = [];

  function sync() {
    var i;
    for (i = 0; i < watching.length; i += 1) {
      watching[i].disconnect();
    }
    watching = [];
    for (i = 0; i < tables.length; i += 1) {
      if (stacked(tables[i])) {
        watching.push(watch(tables[i]));
      } else {
        /* Back to the table layout: hand every row its value back, or a row left mid-fade would stay
           dimmed in a layout that has no concept of a focused card. */
        var rows = tables[i].querySelectorAll('tbody tr');
        var r;
        for (r = 0; r < rows.length; r += 1) {
          rows[r].style.removeProperty('--dc-pt-focus');
          rows[r].__ptFocus = null;
        }
      }
    }
  }

  var resizeFrame = 0;
  window.addEventListener(
    'resize',
    function () {
      if (resizeFrame) {
        return;
      }
      resizeFrame = window.requestAnimationFrame(function () {
        resizeFrame = 0;
        sync();
      });
    },
    { passive: true }
  );

  sync();
})();
